import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Button, SimpleForm, TextInput, SelectInput, maxLength, useEditController, useNotify, useRefresh, useTranslate } from 'react-admin';
import { Grid, Box } from '@mui/material';
import ConfirmToolbar from '@/components/global/buttons/ConfirmToolbar';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import CollectionPointInput from '@/components/newDevice/CollectionPointInput.jsx';
import DeviceGroupInput from '@/components/newDevice/DeviceGroupInput.jsx';
import { collectionTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { DateInput } from '@/components/global/inputs/DateInput.jsx';
import { editTransform } from '@/components/newDevice/forms/editTransform.js';
import { DeploymentStatusInput } from '@/components/newDevice/inputs/DeploymentStatusInput.jsx';
import { ContractorSelect } from '@/components/newDevice/inputs/ContractorSelect.jsx';

export const EditForm = ({ id, onSave, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const onSuccess = useCallback(() => {
        notify('ra.notification.updated', {
            type: 'info',
            messageArgs: { smart_count: 1 },
            undoable: false,
        });
        refresh();
        onSave();
    }, [onSave, notify, refresh]);
    const { record, save, isLoading } = useEditController({
        id,
        mutationMode: 'pessimistic',
        mutationOptions: { onSuccess },
        transform: editTransform,
    });

    const confirmSubmit = (formValues, record) => formValues.deploymentStatus !== record.deploymentStatus;
    const confirmMessage = translate('resources.device.labels.confirmMessageDeploymentStatus');

    if (isLoading) {
        return null;
    }

    const validateCollectionType = (value, allValues) => {
        if (['deployed', 'readyForDeployment'].includes(allValues.deploymentStatus)) {
            if (!value) {
                return translate('common.validation.required');
            }
        }
        return undefined;
    };

    return (
        <SimpleForm
            record={record}
            onSubmit={save}
            mode='onBlur'
            reValidateMode='onBlur'
            toolbar={
                <Box className='popup-wrapper popup-wrapper--footer'>
                    <Button onClick={onClose} color='secondary' startIcon={<ArrowLeftIcon />} label={translate('common.cancel')} />
                    <ConfirmToolbar onSubmit={save} shouldDisplay={confirmSubmit} message={confirmMessage} />
                </Box>
            }
        >
            <Grid container>
                <Box className='popup-wrapper'>
                    <Box className='popup-box'>
                        <TextInput source='serialNumber' label={translate('resources.serviceRequest.labels.serialNumberLabel')} fullWidth={true} disabled />
                        <ContractorSelect disabled />
                        <DeploymentStatusInput />
                        <DateInput
                            className='date-input'
                            source='warrantyDate'
                            label={translate('resources.device.fields.warrantyDate')}
                            name='warrantyDate'
                            fullWidth
                        />
                        <SelectInput
                            source='collectionType'
                            name='collectionType'
                            label={translate('resources.device.fields.collectionType')}
                            choices={collectionTypeChoices(translate)}
                            fullWidth
                            validate={validateCollectionType}
                        />
                    </Box>
                </Box>
                <Box className='popup-wrapper'>
                    <Box className='popup-box'>
                        <DeviceGroupInput sx={{ minWidth: '100%' }} />
                    </Box>
                </Box>
                <Box className='popup-wrapper'>
                    <Box className='popup-box'>
                        <CollectionPointInput />
                    </Box>
                </Box>
                <Box className='popup-wrapper'>
                    <Box className='popup-box'>
                        <TextInput
                            source='description'
                            label={translate('resources.campaign.fields.deviceDescription')}
                            multiline
                            fullWidth
                            validate={maxLength(500, translate('common.validation.maxLength', { count: 500 }))}
                            sx={{ '& textarea': { minHeight: '96px' } }}
                        />
                    </Box>
                </Box>
            </Grid>
        </SimpleForm>
    );
};

EditForm.propTypes = {
    id: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};
